/**
 * `useAlertsAccess()` — does the caller hold `tool.alerts.use`?
 *
 * Every alerts entry point (Home card, Mijn-omgeving card, the alerts page,
 * the dataset "create alert" CTAs) asks this BEFORE it talks to `/v1/alerts`.
 * Without the entitlement those routes answer 403 `entitlement_missing`, which
 * the HTTP client turns into the global UpgradeModal — fine for an explicit
 * action, an ambush for a passive card on Home.
 *
 * Three states, so a card never flashes the teaser at a paying customer:
 *   - `canUseAlerts`  the key is granted (access set, or the JWT claim before
 *                     the first `/me/access` fetch settles);
 *   - `isResolving`   not granted YET and the access set has never loaded and
 *                     has not failed — render a skeleton, call nothing;
 *   - `isLocked`      definitively not granted — render the teaser;
 *   - `accessUnavailable`  the access fetch errored before ever loading and the
 *                     claims do not grant the key — NOT locked: try the load.
 */
import { computed } from 'vue';
import { useAccess } from '@/composables/useAccess';
import { ACCESS_KEYS } from '@/config/entitlementKeys';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';

export function useAlertsAccess({ refreshOnMount = true } = {}) {
  const store = useEntitlementsStore();
  const { can } = useAccess({ refreshOnMount });

  const canUseAlerts = can(ACCESS_KEYS.TOOL_ALERTS);
  const isResolving = computed(
    () =>
      !canUseAlerts.value &&
      !store.accessLoadedAt &&
      (!store.accessError || store.accessError === 'session_expired')
  );
  // The access set never loaded AND its fetch failed: we do not know. Showing
  // the upsell teaser to what may be a paying customer is worse than trying
  // the normal load (with the upgrade modal suppressed) and letting the
  // consumer fall into its own neutral error state if that fails too.
  // `session_expired` is not an outage: the store is already redirecting to the
  // login page, so no list / options call should go out meanwhile.
  const accessUnavailable = computed(
    () =>
      !canUseAlerts.value &&
      !store.accessLoadedAt &&
      !!store.accessError &&
      store.accessError !== 'session_expired'
  );
  const isLocked = computed(
    () => !canUseAlerts.value && !isResolving.value && !accessUnavailable.value
  );
  /** Consumers load when granted, or when access could not be determined. */
  const canAttemptLoad = computed(() => canUseAlerts.value || accessUnavailable.value);

  return { canUseAlerts, isResolving, isLocked, accessUnavailable, canAttemptLoad };
}
